import { inject, Injectable } from '@angular/core';
import { JobApiService } from '@data-terminal/feature-workstation-details/services/job-api/job-api.service';
import { filter, map, Observable, Subject, switchMap, tap } from 'rxjs';
import { Job } from '@data-terminal/shared-models/job';
import { RxState } from '@rx-angular/state';

export interface JobsState {
    searchQuery: string;
    jobs: Job[];
    timestamp: number;
    isFetching: boolean;
}

const CACHE_DURATION = 30_000;

@Injectable({
    providedIn: 'root',
})
export class JobService {
    readonly #jobApiService = inject(JobApiService);
    readonly #state: RxState<JobsState> = inject(RxState<JobsState>);

    private readonly triggerFetchJobs = new Subject<string>();

    private readonly jobsState$ = this.#state.select();

    constructor() {
        this.#state.set({
            searchQuery: '',
            jobs: [],
            timestamp: 0,
            isFetching: true,
        });

        this.#state.hold(
            this.triggerFetchJobs.pipe(
                tap((searchQuery) => {
                    this.#state.set({ jobs: [], searchQuery, timestamp: Date.now() });
                }),
                switchMap((searchQuery) => this.#jobApiService.getJobList(searchQuery)),
                tap((jobs) => {
                    this.#state.set({ jobs: jobs, isFetching: false });
                })
            )
        );
    }

    public getJobList(searchQuery: string): Observable<Job[]> {
        const now = new Date().getTime();
        searchQuery = searchQuery.toLowerCase();
        if (
            searchQuery.toLowerCase().includes(this.#state.get('searchQuery').toLowerCase()) &&
            now < this.#state.get('timestamp') + CACHE_DURATION
        ) {
            return this.getJobListByQuery(searchQuery);
        } else {
            this.#state.set({ isFetching: true });
            this.triggerFetchJobs.next(searchQuery);
            return this.getJobListByQuery(searchQuery);
        }
    }

    private getJobListByQuery(searchQuery: string): Observable<Job[]> {
        return this.jobsState$.pipe(
            filter(({ isFetching }) => !isFetching),
            map(({ jobs }) => {
                return jobs.filter(
                    (job) =>
                        job.jobId.toLowerCase().includes(searchQuery) || job.jobName.toLowerCase().includes(searchQuery)
                );
            })
        );
    }
}
